// 📁 src/pages/InvestorDashboard.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const InvestorDashboard = () => {
  const { user, token } = useAuth();
  const [startups, setStartups] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ sector: '', stage: '', region: '' });
  const [greeting, setGreeting] = useState('');

  // 🎯 Dynamic AI-style greeting
  useEffect(() => {
    if (user?.name) {
      const messages = [
        `👋 Welcome back, ${user.name}! Ready to discover your next unicorn?`,
        `🚀 Hello ${user.name}, let's match you with high-potential startups.`,
        `📈 Hi ${user.name}, explore today's best founder-fit opportunities.`,
        `💡 ${user.name}, here are promising innovations for your portfolio.`,
      ];
      const randomIndex = Math.floor(Math.random() * messages.length);
      setGreeting(messages[randomIndex]);
    }
  }, [user]);

  // 📊 Fetch startup matches
  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const res = await axios.get('/api/investor/matches', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStartups(res.data);
        setFiltered(res.data);
      } catch (err) {
        console.error('❌ Error fetching matches:', err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user?.role === 'investor') fetchMatches();
  }, [user, token]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);

    const result = startups.filter((s) => {
      return (
        (!newFilters.sector || s.sector?.toLowerCase().includes(newFilters.sector.toLowerCase())) &&
        (!newFilters.stage || s.stage?.toLowerCase().includes(newFilters.stage.toLowerCase())) &&
        (!newFilters.region || s.region?.toLowerCase().includes(newFilters.region.toLowerCase()))
      );
    });

    setFiltered(result);
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-2">📊 Investor Dashboard</h2>

      {greeting && (
        <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 text-blue-700 rounded">
          {greeting}
        </div>
      )}

      {loading ? (
        <p className="text-gray-600">⏳ Loading matched startups...</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <input
              name="sector"
              placeholder="Filter by Sector"
              value={filters.sector}
              onChange={handleFilterChange}
              className="p-2 border rounded"
            />
            <input
              name="stage"
              placeholder="Filter by Stage"
              value={filters.stage}
              onChange={handleFilterChange}
              className="p-2 border rounded"
            />
            <input
              name="region"
              placeholder="Filter by Region"
              value={filters.region}
              onChange={handleFilterChange}
              className="p-2 border rounded"
            />
          </div>

          {filtered.length === 0 ? (
            <p className="text-gray-500 italic">No startups match your filters.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((startup, i) => (
                <div key={i} className="border rounded p-4 shadow bg-white hover:shadow-lg transition">
                  <h3 className="text-lg font-semibold text-blue-800">{startup.name}</h3>
                  <p><strong>Sector:</strong> {startup.sector}</p>
                  <p><strong>Stage:</strong> {startup.stage}</p>
                  <p><strong>Region:</strong> {startup.region}</p>
                  <p><strong>Match Score:</strong> {startup.matchScore || 'N/A'}</p>

                  {startup.website && (
                    <p className="mt-1">
                      <a
                        href={startup.website}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 underline"
                      >
                        🌐 Visit Website
                      </a>
                    </p>
                  )}

                  {startup.pitchDeck && (
                    <p className="mt-1">
                      <a
                        href={startup.pitchDeck}
                        target="_blank"
                        rel="noreferrer"
                        className="text-green-600 underline"
                      >
                        📄 View Pitch Deck
                      </a>
                    </p>
                  )}

                  <button
                    disabled
                    className="mt-3 px-4 py-2 bg-gray-200 text-gray-600 rounded w-full cursor-not-allowed"
                    title="Coming Soon"
                  >
                    🤖 Ask AI Assistant (coming soon)
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InvestorDashboard;
